import { Box, Button, Card, Divider, DropdownSelect, IconButton, Menu, Text, Tooltip } from '@zillow/constellation-10';
import { IconCloseFilled, IconInfoFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import { PlacementSource } from '@zg-rentals/ts-rental-manager-api-client';

import { useAutoSpotlight } from './AutoSpotlightContext';
import { OPTIMIZER_LIST } from './rabDeployment';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';
import { getCurrentPlacementSource } from './utils/cacheHelpers';

import type { OptimizerId } from './rabDeployment';

const INFO_ICON = (
  <IconButton
    title="Info"
    size="sm"
    emphasis="bare"
    shape="circle"
    css={{ color: 'icon.muted', width: 'icon.sm', height: 'icon.sm' }}
    icon={<IconInfoFilled />}
  />
);

export function OptimizeForScopeCard() {
  const ctx = useAutoSpotlight();
  const queryClient = useQueryClient();
  if (!ctx) {
    return null;
  }

  const {
    availableCount,
    autoCount,
    batchExclude,
    batchPin,
    batchUnexclude,
    batchUnpin,
    clearSelection,
    excludedIds,
    isOverviewLoaded,
    isSelectionMode,
    onChangeOptimizer,
    optimizer,
    pinnedCount,
    pinnedIds,
    selectedIds,
    toggleSelectionMode,
  } = ctx;

  const options = OPTIMIZER_LIST.map((o) => ({ label: o.label, value: o.id, meta: o.description }));
  const selectedCount = selectedIds.size;
  const selectedAliases = Array.from(selectedIds);

  // Use advertising query cache as the source of truth for placement status (same as SpotlightControlCell).
  // Fall back to context's optimistic sets for Storybook / before the per-listing queries have loaded.
  const isPinned = (id: string) => {
    const cached = getCurrentPlacementSource(queryClient, id);
    return cached != null ? cached === PlacementSource.PINNED : pinnedIds.has(id);
  };
  const isExcluded = (id: string) => {
    const cached = getCurrentPlacementSource(queryClient, id);
    return cached != null ? cached === PlacementSource.EXCLUDED : excludedIds.has(id);
  };

  // Batch actions are only shown when the action is applicable to the entire selection.
  // Pin: hidden if any selected listing is already pinned; disabled if selection exceeds available slots.
  // Unpin: shown only if every selected listing is pinned.
  // Exclude: hidden if any selected listing is already excluded.
  // Unexclude: shown only if every selected listing is excluded.
  const anyPinned = selectedAliases.some(isPinned);
  const allPinned = selectedAliases.length > 0 && selectedAliases.every(isPinned);
  const anyExcluded = selectedAliases.some(isExcluded);
  const allExcluded = selectedAliases.length > 0 && selectedAliases.every(isExcluded);
  const overLimit = isOverviewLoaded && selectedCount > availableCount;

  const bulkActions: Array<{ label: string; onSelect: () => void; disabled?: boolean }> = [
    ...(!anyPinned ? [{ label: 'Pin', onSelect: () => batchPin(selectedAliases), disabled: overLimit }] : []),
    ...(allPinned ? [{ label: 'Unpin', onSelect: () => batchUnpin(selectedAliases) }] : []),
    ...(!anyExcluded ? [{ label: 'Exclude', onSelect: () => batchExclude(selectedAliases) }] : []),
    ...(allExcluded ? [{ label: 'Unexclude', onSelect: () => batchUnexclude(selectedAliases) }] : []),
  ];

  return (
    <Card css={{ marginBlock: '400', padding: '500' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
          <Text textStyle="heading-sm">Spotlight</Text>
          <Tooltip content={COPY.exposureCopy} trigger={INFO_ICON} />
        </Box>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
          <Text textStyle="body-sm">
            {availableCount} Available spot{availableCount === 1 ? '' : 's'} • {pinnedCount} Pinned · {autoCount} Auto
            deployed
          </Text>
          <Tooltip content={COPY.tooltips.summaryCounts} trigger={INFO_ICON} />
        </Box>
      </Box>

      <Divider css={{ marginBlock: '400' }} />

      <Box css={{ display: 'flex', alignItems: 'center', gap: '300', flexWrap: 'wrap' }}>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '300', flexShrink: 0 }}>
          <Tooltip content={COPY.tooltips.optimizeBy} trigger={INFO_ICON} />
          <Text textStyle="body-sm-bold" css={{ color: '{colors.text.interactive}' }}>
            Optimize by
          </Text>
          <DropdownSelect
            value={optimizer}
            options={options}
            onChange={(value) => onChangeOptimizer((Array.isArray(value) ? value[0] : value) as OptimizerId)}
            showLabelForValue
            size="sm"
            css={{ width: '220px' }}
          />
        </Box>

        {isSelectionMode ? (
          <Box css={{ display: 'flex', alignItems: 'center', gap: '200', marginInlineStart: 'auto' }}>
            <Text textStyle="body-sm">{selectedCount} selected</Text>
            <IconButton
              title="Clear selection"
              size="sm"
              emphasis="bare"
              shape="circle"
              icon={<IconCloseFilled />}
              onClick={clearSelection}
            />
            {selectedCount > 0 && bulkActions.length > 0 && (
              <Menu
                trigger={
                  <Button size="sm" emphasis="outlined">
                    Action ▾
                  </Button>
                }
                content={bulkActions.map(({ label, onSelect, disabled }) => (
                  <Menu.Item key={label} onClick={disabled ? undefined : onSelect} disabled={disabled}>
                    <Menu.ItemLabel>{label}</Menu.ItemLabel>
                  </Menu.Item>
                ))}
              />
            )}
          </Box>
        ) : (
          <Button size="sm" emphasis="outlined" onClick={toggleSelectionMode} css={{ marginInlineStart: 'auto' }}>
            Select
          </Button>
        )}
      </Box>
    </Card>
  );
}

export default OptimizeForScopeCard;
